import { getDocumentTypeSign } from "../constants/documentType";
import { calcularCobradoEsperado } from "./cierreCalculations";
import { addMoney, resolveMoneyDecimals, roundMoney, subtractMoney } from "./money";

// Lo que lleva cobrado un turno, desglosado por forma de pago.
//
// Es el cálculo que alimenta la columna "Cobrado" del cierre --lo que la caja
// debería tener, contra lo que el cajero cuenta-- y ahora también la pantalla
// "Cómo va el turno", que lo enseña sin cerrar nada.
//
// Vive acá, y no dentro de CierreDeCajaForm.js, por la misma razón que
// calcularCobradoEsperado: la única vez que este cálculo estuvo escrito dos
// veces, las dos copias se desincronizaron y una pantalla anunciaba un
// sobrante falso del tamaño exacto del fondo de apertura. Una consulta del
// turno que no coincida con el cierre que viene después no sirve para nada:
// el cajero la usaría para prepararse y se encontraría otro número al cerrar.

/** Los códigos de forma de pago se guardan en mayúsculas y sin espacios. */
export const normalizarCodigo = (valor) => (valor || "").toString().trim().toUpperCase();

/**
 * Cuál de las formas de pago del turno se lleva el fondo de apertura.
 *
 * El fondo queda físicamente en un solo cajón, junto con las ventas en
 * efectivo, así que hay que sumarlo a una sola fila para poder cuadrar contra
 * el conteo físico -- sumarlo en más de una lo contaría varias veces.
 *
 * **Antes se decidía por el código de la forma de pago** (sólo si se llamaba
 * literalmente "EFECTIVO"), y eso dejó de alcanzar el día que una tienda cobra
 * en efectivo con códigos propios (Perú: `EFE-1310`, `EFE-1353`..., uno por
 * caja/cuenta bancaria) -- ninguno se llama "EFECTIVO", así que ninguna fila se
 * llevaba el fondo y la caja no se podía cuadrar. Acuerdo del 23/09/2026: se
 * decide por `Tipo_Pago`, tomando la primera forma de pago del turno (en el
 * mismo orden en que se lista) cuyo tipo sea Efectivo. Con eso alinea con
 * `Reports.tsx` (Cuadre por forma de pago), que ya decidía por tipo.
 *
 * @param {string[]} codigos Los códigos del turno, en el orden en que se listan.
 * @param {Map<string, object>} catalogo Código normalizado -> forma de pago.
 * @returns {string|null} El código que se lleva el fondo, o null si ninguna
 *   forma de pago del turno es de tipo Efectivo.
 */
export const codigoDelFondoDeApertura = (codigos, catalogo) =>
  codigos.find((codigo) => (catalogo.get(codigo)?.Tipo_Pago || "").toUpperCase() === "EFECTIVO") ??
  null;

/**
 * @param {Object}  params
 * @param {Array}   params.pagos            Líneas de pago del rango (paymentMethodCode, Amount, documentType).
 * @param {Array}   params.formasDePago     Catálogo local (Code, Description, Tipo_Pago).
 * @param {number}  params.importeApertura  Fondo con el que se abrió la caja.
 * @param {Object}  [params.country]        País activo: precisión de cálculo y signo del documento.
 * @returns {{metodos: Array, totales: Object}}
 */
export const resumirElTurno = ({
  pagos = [],
  formasDePago = [],
  importeApertura = 0,
  country = null,
} = {}) => {
  const decimals = resolveMoneyDecimals(country);
  const catalogo = new Map(
    (formasDePago || [])
      .filter((forma) => normalizarCodigo(forma?.Code))
      .map((forma) => [normalizarCodigo(forma.Code), forma])
  );

  // Lo cobrado y lo devuelto, por código de forma de pago.
  const porCodigo = (pagos || []).reduce((acc, pago) => {
    const codigo = normalizarCodigo(pago?.paymentMethodCode);
    if (!codigo) return acc;

    const importe = Math.abs(roundMoney(Number(pago?.Amount) || 0, decimals));
    const devuelve = getDocumentTypeSign(pago?.documentType, country?.code) < 0;
    const previo = acc[codigo] || { facturado: 0, notasCredito: 0 };

    acc[codigo] = {
      facturado: devuelve ? previo.facturado : addMoney(previo.facturado, importe, decimals),
      notasCredito: devuelve
        ? addMoney(previo.notasCredito, importe, decimals)
        : previo.notasCredito,
    };
    return acc;
  }, {});

  // La unión de las dos listas: una forma de pago del catálogo que no se usó
  // tiene que salir en cero --el cajero cuenta lo que hay en el cajón, no lo
  // que se vendió-- y una que se usó pero ya no está en el catálogo tampoco
  // puede desaparecer, o su dinero no se cuadraría contra nada.
  const codigos = [...new Set([...catalogo.keys(), ...Object.keys(porCodigo)])];
  const codigoDelFondo = codigoDelFondoDeApertura(codigos, catalogo);

  const metodos = codigos
    .map((codigo) => {
      const forma = catalogo.get(codigo);
      const { facturado = 0, notasCredito = 0 } = porCodigo[codigo] || {};
      const cobro = subtractMoney(facturado, notasCredito, decimals);
      const esFondoDeApertura = codigo === codigoDelFondo;

      return {
        codigo,
        nombre: forma?.Description || forma?.Code || codigo,
        tipoPago: forma?.Tipo_Pago || "",
        esEfectivo: (forma?.Tipo_Pago || "").toUpperCase() === "EFECTIVO",
        // A qué fila del turno se le suma el fondo de apertura -- ver
        // codigoDelFondoDeApertura. Nunca es true en más de una fila.
        esFondoDeApertura,
        enElCatalogo: Boolean(forma),
        facturado,
        notasCredito,
        cobro,
        esperado: roundMoney(
          calcularCobradoEsperado(esFondoDeApertura, cobro, Number(importeApertura) || 0),
          decimals
        ),
      };
    })
    .sort((a, b) => a.nombre.localeCompare(b.nombre));

  const sumar = (campo) =>
    metodos.reduce((total, metodo) => addMoney(total, metodo[campo], decimals), 0);

  return {
    metodos,
    totales: {
      facturado: sumar("facturado"),
      notasCredito: sumar("notasCredito"),
      cobro: sumar("cobro"),
      esperado: sumar("esperado"),
    },
  };
};
